<template>
  <div
    v-if="marketPosition"
    class="market-position"
    data-cy="market-position"
  >
    <!-- The band's endpoints, aligned over the bar's ends. The trailing "+" on the upper
         bound signals that winning bids extend above it - that's the number to beat.

         These percentiles are measured over auctions a call ACTUALLY ARRIVED ON, but the
         copy keeps saying "winning bids": on this UI a winning bid means a delivered call
         (owner decision, docs/MARKET_CONTEXT_DELIVERED_CALLS.md). The rank band in the
         next column is scored against a genuinely different population - every buyer's
         typical bid, delivered or not - and that gap is what the pair of column-header
         tooltips exists to name. -->
    <div
      v-b-tooltip.hover
      class="market-position__range text-muted"
      :title="priceRangeTooltip"
      data-cy="text-price-range"
    >
      <span>{{ formatCurrency(marketPosition.marketP25InUSD) }}</span>
      <span>{{ formatCurrency(marketPosition.marketP75InUSD) }}+</span>
    </div>

    <div class="market-position__bar">
      <div
        class="market-position__band"
        :style="bandStyle"
      />
      <!-- No marker without a bid to mark: a dynamic campaign that placed no bids in the
           window still gets the market band, but planting a marker anywhere on it would
           read as a bid the campaign never made -->
      <div
        v-if="bidInUsd != null"
        class="market-position__marker"
        :class="tierTextClass"
        :style="markerStyle"
      />
    </div>

    <div class="market-position__labels">
      <b-badge
        v-if="marketPosition.tier"
        :variant="getBadgeVariantForBidTier(marketPosition.tier)"
        data-cy="badge-bid-tier"
      >
        {{ getBidTierText(marketPosition.tier) }}
      </b-badge>
      <span
        v-else
        class="text-muted"
        data-cy="text-no-bids"
      >
        {{ noBidsText }}
      </span>
      <!-- Same guard as the marker: a campaign that placed no bids in the window must not get
           a "Your bid" line either, for the same reason it gets no marker -->
      <span
        v-if="bidInUsd != null"
        class="market-position__own-bid"
        data-cy="text-own-bid"
      >
        {{ ownBidLabel }} {{ formatCurrency(bidInUsd) }}
      </span>
    </div>
  </div>

  <div
    v-else
    class="text-muted"
  >
    -
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'
import { getBadgeVariantForBidTier, getBidTierText, toCents } from '@/store/modules/market-context.store'

export default {
  name: 'MarketPositionIndicator',

  props: {
    // Market position row for one campaign, see MarketContextAPI.getCampaignMarketPositions
    marketPosition: {
      type: Object,
      default: null
    },

    // The bid to mark on the band - the row's positionedBidInUSD. Null when a dynamic
    // campaign placed no bids in the window, and then the band renders with no marker
    bidInUsd: {
      type: Number,
      default: null
    },

    // The window the percentiles and the no-bids verdict were measured over, as the `short`
    // form of formatMarketWindow ('24h' or '7d'). "No bids (24h)" and "No bids (7d)" are very
    // different statements about a campaign, so the band must not claim one when it means the
    // other
    windowShort: {
      type: String,
      default: ''
    },

    // Static campaigns configure a single fixed bid, so bidInUsd IS that number - "Your bid"
    // is exact. RTB/Programmatic campaigns don't set one; bidInUsd is the median of what they
    // actually placed in the window, so "Your bid" would overstate it as a fixed number.
    // "Your typical bid" says what it actually is
    isStatic: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    // Bare until the window is known, rather than briefly claiming the wrong one
    noBidsText() {
      return this.windowShort ? `No bids (${this.windowShort})` : 'No bids'
    },

    ownBidLabel() {
      return this.isStatic ? 'Your bid' : 'Your typical bid'
    },

    // "Winning" is load-bearing: the percentiles are measured over auction WINNERS, so a bare
    // "bids" would read as what campaigns like yours offer, a lower and less demanding number
    // than what it takes to win. Since 2026-09-04 those winners are additionally restricted to
    // auctions a call actually arrived on - but the wording stays, because on this UI a winning
    // bid means a delivered call. Names the window only once it is known, for the same reason
    // noBidsText does
    priceRangeTooltip() {
      const low = this.formatCurrency(this.marketPosition.marketP25InUSD)
      const high = this.formatCurrency(this.marketPosition.marketP75InUSD)
      const windowPhrase = this.windowShort ? ` in the last ${this.windowShort}` : ''

      return `Winning bids in this market${windowPhrase} mostly fell between ${low} and ${high} - top campaigns bid above ${high}`
    },

    // The bar spans half an interquartile range beyond P25/P75 on each side, so the
    // bid marker stays visible for bids slightly outside the market band
    scale() {
      const p25 = this.marketPosition.marketP25InUSD
      const p75 = this.marketPosition.marketP75InUSD
      const padding = Math.max((p75 - p25) * 0.5, 1)

      return {
        min: p25 - padding,
        max: p75 + padding
      }
    },

    // Thin cells hit this more often since the percentiles count delivered calls only
    isFlatMarket() {
      return toCents(this.marketPosition.marketP25InUSD) === toCents(this.marketPosition.marketP75InUSD)
    },

    bandStyle() {
      // A flat market has no interquartile range to fill, and a zero-width band reads as a
      // missing bar - draw a dot on the price instead, as BidMarketContextPanel does. The dot is
      // as wide as the bar is tall, which with the band's border radius makes it round.
      if (this.isFlatMarket) {
        return {
          left: `${this.positionPercent(this.marketPosition.marketP25InUSD)}%`,
          width: '6px',
          transform: 'translateX(-50%)'
        }
      }

      return {
        left: `${this.positionPercent(this.marketPosition.marketP25InUSD)}%`,
        width: `${this.positionPercent(this.marketPosition.marketP75InUSD) - this.positionPercent(this.marketPosition.marketP25InUSD)}%`
      }
    },

    // Only read when the marker renders, i.e. when bidInUsd is non-null - deliberately
    // no fallback bid, so a missing bid can never resolve to a marker position
    markerStyle() {
      return {
        left: `${this.positionPercent(this.bidInUsd)}%`
      }
    },

    tierTextClass() {
      return `text-${this.getBadgeVariantForBidTier(this.marketPosition.tier)}`
    }
  },

  methods: {
    getBadgeVariantForBidTier,

    getBidTierText,

    formatCurrency,

    positionPercent(value) {
      const percent = ((value - this.scale.min) / (this.scale.max - this.scale.min)) * 100
      return Math.min(100, Math.max(0, percent))
    }
  }
}
</script>

<style lang="scss" scoped>
.market-position__bar {
  position: relative;
  width: 150px;
  height: 6px;
  border-radius: 3px;
  background-color: $violet-dark;
}

.market-position__band {
  position: absolute;
  top: 0;
  height: 6px;
  border-radius: 3px;
  background-color: rgba($blue, 0.45);
}

.market-position__marker {
  position: absolute;
  top: -3px;
  width: 2px;
  height: 12px;
  // Inherits the tier's Bootstrap text-* color so it always matches the badge
  background-color: currentColor;
}

.market-position__labels {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
  white-space: nowrap;
}

// Deliberately not text-muted while __range is: the buyer's own number is the one they
// came to read, so it wins the contrast against the market reference
.market-position__own-bid {
  font-size: 0.75rem;
}

// Same width as the bar so the two labels align with its ends
.market-position__range {
  display: flex;
  justify-content: space-between;
  width: 150px;
  margin-bottom: 2px;
  font-size: 0.75rem;
}
</style>
